import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function NotesTab({ project }) {
  const { readOnly } = useAuth();
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/projects/${project.id}/notes`).then((r) => setNotes(r.data)), [project.id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/notes`, { text: text.trim() });
      setText("");
      toast.success("Note added");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (n) => {
    if (!window.confirm("Remove this note?")) return;
    try { await api.delete(`/notes/${n.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="notes-tab">
      <div className="space-y-3" data-testid="notes-list">
        {notes?.length === 0 && <div data-testid="notes-empty-state" className="rounded-md border border-dashed border-gray-300 bg-gray-50 py-8 text-center text-sm text-gray-400">No notes recorded</div>}
        {notes?.map((n) => (
          <div key={n.id} data-testid={`note-${n.id}`} className="flex justify-between gap-4 rounded-md border border-gray-200 bg-gray-50/70 p-4">
            <div>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{n.text}</p>
              <p className="mt-2 font-mono-data text-[10px] uppercase text-gray-400">{n.created_by} · {fmtDateTime(n.created_at)}</p>
            </div>
            {!readOnly && (
              <button data-testid={`note-delete-${n.id}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-gray-300 transition-colors duration-150 hover:border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => remove(n)} aria-label="Remove note"><Trash2 size={14} strokeWidth={1.5} /></button>
            )}
          </div>
        ))}
      </div>
      {notes?.length !== undefined && (
        <form onSubmit={add} className="mt-4 rounded-md border border-gray-300 bg-gray-50 p-4" data-testid="add-note-form">
          <label htmlFor="note-input" className="bdx-label mb-2 block">Add Note</label>
          <textarea id="note-input" data-testid="note-input" className="bdx-input bg-white" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Record site updates, calls with distributor, retailer follow-ups…" />
          <div className="mt-3 flex justify-end">
            <button data-testid="note-submit" disabled={busy || !text.trim()} className="bdx-btn-primary">Add Note</button>
          </div>
          {readOnly && <p className="mt-2 text-[10px] text-gray-400">BLG may post notes to the project diary. Existing notes cannot be edited or deleted.</p>}
        </form>
      )}
    </div>
  );
}
